/**  
 * EASY_RENTERS_LandingPage_v02O_WIZARD (React + TypeScript + React-Bootstrap)
 * Version: v02O
 * Date: 2025-08-29 (America/Denver)
 *
 * Changes vs v02N:
 * - Improved confirmation screen formatting (sectioned summary, no JSON).
 * - Keeps modular submit pipeline (typed -> wire strings -> post).
 * - Works in Wizard (Step 4) and Vertical modes.
 *
 * Mobile UX Updates (DOCUMENTED):
 * - [CHANGE] Navbar: add a mobile-visible "Call" button OUTSIDE the collapsed area so it's always visible on small screens.
 * - [CHANGE] Add a fixed bottom mobile call bar so the call-to-agent is reachable while scrolling.
 * - [CHANGE] Foreground Container => fluid with trimmed paddings/gutters on phones.
 * - [CHANGE] Reduce large padding (p-5) on phones; scale up on larger screens.
 * - [CHANGE] Add .card-edge-mobile class to make the form card feel "full-bleed" on extra small screens.
 * - [CHANGE] Hero height responsive: shorter on phones so the form appears sooner.
 * - [CHANGE] Optional: form column appears first on phones (order-first) for quicker access.
 */

import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  Row,
  Spinner,
  InputGroup,
  ProgressBar,
  Carousel,
} from "react-bootstrap";

import { rentersInsuranceFAQs } from "../../component-demos/DUI-Insurance/FAQ_RentersInsurance_v01";
import { FAQ_Component_v02b_Simple } from "../../component-library/FAQ_Component_v02b-simple";

/* ============================================================
   CONFIG
============================================================ */
const VERSION = "v02O";
const SHOW_VERSION = false;
const TEST_MODE = false; // true => logs payload, skips network
const API_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback";
const AGENT_PHONE_NUMBER = "(303) 337-4433";
const imagePath = "insurance/images/RENTERS-Insurance/";

/* ============================================================
   TYPES
============================================================ */
type ResidenceType = "" | "apartment" | "condo" | "house" | "townhome" | "duplex" | "other";
type Deductible = "" | "250" | "500" | "1000" | "1500" | "2500";

/** Raw lead payload (typed for app state) */
interface ILeadPayload {
  fullName: string;
  email: string;
  phone: string;

  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;

  residenceType: ResidenceType;
  moveInDate?: string;

  personalPropertyCoverage: string;
  liabilityCoverage: string;
  deductible: Deductible;

  pets?: string;
  roommatesCount: number;
  priorClaimsCount5y: number;

  yearBuilt?: string;
  unitFloor?: string;
  hasSmokeAlarms?: boolean;
  hasDeadbolts?: boolean;
  hasMonitoredAlarm?: boolean;
  landlordName?: string;

  notes?: string;

  utm?: { source?: string; medium?: string; campaign?: string };

  domainName: string;
  itemCategory: string;
  itemClass: string;
  itemType: string;
  clientVersion: string;
}

/** Wire payload where EVERY VALUE IS A STRING */
type WirePayload = Record<string, string>;

/* ============================================================
   HELPERS
============================================================ */
const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

function digitsOnly(s: string): string {
  return (s ?? "").replace(/\D+/g, "");
}
function isValidEmail(email: string): boolean {
  return /.+@.+\..+/.test(email);
}
function isValidPhone(raw: string): boolean {
  return digitsOnly(raw).length === 10;
}
function isValidZipCode(raw: string): boolean {
  return digitsOnly(raw).length === 5;
}
function formatPhonePretty(raw: string): string {
  const d = digitsOnly(raw);
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
function cap(s: string) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
function money(nLike: string) {
  const n = Number(nLike);
  if (!Number.isFinite(n)) return nLike;
  try {
    return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  } catch {
    return `$${n}`;
  }
}
const dash = (v?: string) => (v && v.trim().length ? v : "—");

/* ============================================================
   SLIDES
============================================================ */
interface Slide {
  src: string;
  alt: string;
  title: string;
  subline: string;
  smallCaption: string;
}
const slides: Slide[] = [
  {
    src: imagePath + "AdobeStock_1452897598_Preview.jpeg",
    alt: "Living room in an apartment—personal property protected.",
    title: "Protect What You Own",
    subline: "Affordable renters coverage for your stuff and your peace of mind.",
    smallCaption: "Fast quotes. Clear options.",
  },
  {
    src: imagePath + "AdobeStock_976347785_Preview.jpeg",
    alt: "Moving boxes in a new apartment.",
    title: "Moving In or Switching?",
    subline: "Tell us about your place—get matched with the right coverage.",
    smallCaption: "Flexible limits. Simple deductibles.",
  },
  {
    src: imagePath + "AdobeStock_473996822_Preview-FLIP.jpg",
    alt: "Hand with keys outside apartment building.",
    title: "Liability & Personal Property",
    subline: "Bundle liability with your property for well-rounded protection.",
    smallCaption: "Smart coverage, fair price.",
  },
];

/* ============================================================
   MOBILE CSS injected for convenience (can be moved to your CSS bundle)
============================================================ */
/**
 * NOTE: We inject CSS here so this file is self-contained for your testing.
 * In production, move these rules into your SCSS/CSS and ensure they load after Bootstrap.
 */
const MOBILE_CSS = `
/* [CHANGE] Responsive hero height: shorter on phones so the form shows sooner */
.hero { min-height: 760px; }
@media (max-width: 576px) {
  .hero { min-height: 540px; }
  .badge { font-size: 0.725rem; } /* subtle badge sizing tweak on phones */
}

/* [CHANGE] Edge-to-edge feel for the form card on extra small screens */
@media (max-width: 576px) {
  .card-edge-mobile {
    margin-left: -0.5rem;   /* counteracts xs container padding (px-2) */
    margin-right: -0.5rem;
    border-radius: 0;
  }
}

/* [CHANGE] Fixed bottom call bar, always visible on mobile; respects iOS safe area */
.mobile-callbar {
  position: fixed;
  left: 0; right: 0; bottom: 0;
  z-index: 1030; /* above navbar & content */
  padding-bottom: env(safe-area-inset-bottom, 0);
  background: rgba(255,255,255,0.9);
  backdrop-filter: saturate(180%) blur(8px);
  border-top: 1px solid rgba(0,0,0,0.075);
}
.mobile-callbar-inner {
  padding: 0.5rem 0.75rem;
}
`;

/* ============================================================
   MAIN
============================================================ */
export const EASY_RENTERS_LandingPage_v02P_WIZARD: React.FC = () => {
  const formRef = useRef<HTMLDivElement | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Contact
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // Address
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");

  // Basics
  const [residenceType, setResidenceType] = useState<ResidenceType>("");
  const [moveInDate, setMoveInDate] = useState<string>("");

  // Coverage
  const [personalPropertyCoverage, setPersonalPropertyCoverage] = useState<string>("30000");
  const [liabilityCoverage, setLiabilityCoverage] = useState<string>("300000");
  const [deductible, setDeductible] = useState<Deductible>("500");

  // Household / risk
  const [pets, setPets] = useState<string>("");
  const [roommatesCount, setRoommatesCount] = useState<number>(0);
  const [priorClaimsCount5y, setPriorClaimsCount5y] = useState<number>(0);

  // Property details
  const [yearBuilt, setYearBuilt] = useState<string>("");
  const [unitFloor, setUnitFloor] = useState<string>("");
  const [hasSmokeAlarms, setHasSmokeAlarms] = useState<boolean>(true);
  const [hasDeadbolts, setHasDeadbolts] = useState<boolean>(true);
  const [hasMonitoredAlarm, setHasMonitoredAlarm] = useState<boolean>(false);
  const [landlordName, setLandlordName] = useState<string>("");

  // Misc
  const [notes, setNotes] = useState<string>("");

  // Submit / server state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);
  const [serverStatus, setServerStatus] = useState<null | { ok: boolean; status?: number; id?: string; message?: string }>(null);

  // Keep the exact data sent (for confirmation display)
  const [lastSubmittedApp, setLastSubmittedApp] = useState<ILeadPayload | null>(null);

  // Mode + wizard
  const [mode, setMode] = useState<"wizard" | "vertical">("wizard");
  const isWizard = mode === "wizard";
  const [currentStep, setCurrentStep] = useState(0);
  const steps = ["Contact & Address", "Coverage & Household", "Property & Notes", "Confirmation"] as const;

  /* ---------- Touched + validation helpers (blur-driven) ---------- */
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const isTouched = (name: string) => !!touched[name];
  const markTouched = (name: string) => setTouched((t) => ({ ...t, [name]: true }));

  const vp = (name: string, pass: boolean) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && pass,
    isInvalid: isTouched(name) && !pass,
    className: isTouched(name) && pass ? "bg-success-subtle" : undefined,
  });

  // Optional-field validation props: green when filled & valid; neutral when blank; never red on blank.
  const vpOpt = (name: string, valuePresent: boolean, passIfPresent: boolean = true) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && valuePresent && passIfPresent,
    isInvalid: isTouched(name) && valuePresent && !passIfPresent,
    className: isTouched(name) && valuePresent && passIfPresent ? "bg-success-subtle" : undefined,
  });

  // Validators (blank optional fields pass)
  const V = {
    fullName: fullName.trim().length >= 2,
    email: isValidEmail(email),
    phone: isValidPhone(phone),

    address1: address1.trim().length >= 3,
    city: city.trim().length >= 2,
    state: state !== "",
    zip: isValidZipCode(zipCode),
    residenceType: residenceType !== "",

    // coverage numbers must be > 0
    personalPropertyCoverage: Number(digitsOnly(personalPropertyCoverage)) > 0,
    liabilityCoverage: Number(digitsOnly(liabilityCoverage)) >= 100000,
    deductible: deductible !== "",

    roommatesCount: Number.isFinite(roommatesCount) && roommatesCount >= 0 && roommatesCount <= 10,
    priorClaimsCount5y: Number.isFinite(priorClaimsCount5y) && priorClaimsCount5y >= 0 && priorClaimsCount5y <= 10,

    // optional but validate format when provided
    yearBuilt: !yearBuilt || /^\d{4}$/.test(yearBuilt),
    unitFloor: !unitFloor || /^\d{1,2}$/.test(unitFloor),
    landlordName: !landlordName || landlordName.trim().length >= 2,

    // Pets: blank OK (neutral). If present, require >= 2 chars for green.
    pets: pets.trim().length === 0 || pets.trim().length >= 2,

    // Notes optional
    notes: notes.trim().length === 0 || notes.trim().length >= 2,
  };

  const valid = useMemo(() => {
    return (
      V.fullName &&
      V.email &&
      V.phone &&
      V.address1 &&
      V.city &&
      V.state &&
      V.zip &&
      V.residenceType &&
      V.personalPropertyCoverage &&
      V.liabilityCoverage &&
      V.deductible &&
      V.roommatesCount &&
      V.priorClaimsCount5y &&
      V.yearBuilt &&
      V.unitFloor &&
      V.landlordName &&
      V.pets &&
      V.notes
    );
  }, [V]);

  function isStepValid(stepIdx: number): boolean {
    switch (stepIdx) {
      case 0:
        return V.fullName && V.email && V.phone && V.address1 && V.city && V.state && V.zip && V.residenceType;
      case 1:
        return V.personalPropertyCoverage && V.liabilityCoverage && V.deductible && V.roommatesCount && V.priorClaimsCount5y && V.pets;
      case 2:
        return V.yearBuilt && V.unitFloor && V.landlordName && V.notes;
      case 3:
        return submitOk === true;
      default:
        return false;
    }
  }

  const goNext = () => {
    const touchMany = (names: string[]) =>
      setTouched((t) => names.reduce((acc, n) => ({ ...acc, [n]: true }), t));
    if (currentStep === 0) touchMany(["fullName", "email", "phone", "address1", "city", "state", "zip", "residenceType"]);
    else if (currentStep === 1) touchMany(["personalPropertyCoverage", "liabilityCoverage", "deductible", "roommatesCount", "priorClaimsCount5y", "pets"]);
    else if (currentStep === 2) touchMany(["yearBuilt", "unitFloor", "landlordName", "notes"]);

    if (!isStepValid(currentStep)) return;
    setCurrentStep((s) => Math.min(3, s + 1));
  };
  const goBack = () => setCurrentStep((s) => Math.max(0, s - 1));

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  /* ============================================================
     PAYLOAD & SUBMIT (MODULARIZED)
  ============================================================ */

  function buildLeadPayload(): ILeadPayload {
    return {
      fullName: fullName.trim(),
      email: email.trim(),
      phone: digitsOnly(phone),

      address1: address1.trim(),
      address2: address2?.trim() || "",
      city: city.trim(),
      state: String(state),
      zipCode: String(zipCode),

      residenceType,
      moveInDate: moveInDate || "",

      personalPropertyCoverage: String(Number(digitsOnly(personalPropertyCoverage))),
      liabilityCoverage: String(Number(digitsOnly(liabilityCoverage))),
      deductible,

      pets: pets?.trim() || "",
      roommatesCount: Number(roommatesCount),
      priorClaimsCount5y: Number(priorClaimsCount5y),

      yearBuilt: yearBuilt || "",
      unitFloor: unitFloor || "",
      hasSmokeAlarms,
      hasDeadbolts,
      hasMonitoredAlarm,
      landlordName: landlordName?.trim() || "",

      notes: notes?.trim() || "",

      // utm: { ... } // optional

      domainName: typeof window !== "undefined" ? String(window.location.hostname) : "unknown",
      itemCategory: "Customer.renters.quote",
      itemClass: "quote.request",
      itemType: "legacy.renters.landing",
      clientVersion: String(VERSION),
    };
  }

  function toWirePayloadStrings(p: ILeadPayload): WirePayload {
    const out: WirePayload = {};
    for (const [k, v] of Object.entries(p)) {
      if (typeof v === "string") {
        out[k] = v;
      } else if (typeof v === "number") {
        out[k] = Number.isFinite(v) ? `${v}` : "";
      } else if (typeof v === "boolean") {
        out[k] = v ? "true" : "false";
      } else if (v && typeof v === "object") {
        try {
          out[k] = JSON.stringify(v);
        } catch {
          out[k] = "";
        }
      } else {
        out[k] = "";
      }
    }
    return out;
  }

  async function postLead(payload: WirePayload): Promise<{ ok: boolean; status: number; id?: string; message?: string }> {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      mode: "cors",
    });

    let resJson: unknown = null;
    try {
      const txt = await res.text();
      resJson = txt ? JSON.parse(txt) : null;
    } catch {
      resJson = null;
    }

    const asObj = (x: unknown): Record<string, unknown> | null =>
      x && typeof x === "object" && !Array.isArray(x) ? (x as Record<string, unknown>) : null;

    if (!res.ok) {
      const o = asObj(resJson);
      const msg = (o?.error as string) || (o?.message as string) || `Request failed: ${res.status}`;
      return { ok: false, status: res.status, message: msg };
    }

    const o = asObj(resJson);
    const id = (o?.id as string) || (o?.requestId as string) || undefined;
    return { ok: true, status: res.status, id };
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    // In wizard mode, ignore submit on steps 0–1 and just advance
    if (isWizard && currentStep < 2) {
      goNext();
      return;
    }

    setSubmitError(null);
    setSubmitOk(false);
    setServerStatus(null);

    // Touch all prior fields so feedback appears if invalid
    setTouched((t) => ({
      ...t,
      fullName: true, email: true, phone: true,
      address1: true, city: true, state: true, zip: true, residenceType: true,
      personalPropertyCoverage: true, liabilityCoverage: true, deductible: true, roommatesCount: true, priorClaimsCount5y: true, pets: true,
      yearBuilt: true, unitFloor: true, landlordName: true, notes: true,
    }));

    if (!valid) {
      setSubmitError("Please complete all required fields.");
      return;
    }

    // 1) Build typed app payload
    const appPayload = buildLeadPayload();

    // 2) Convert to string-only wire payload
    const wirePayload = toWirePayloadStrings(appPayload);

    try {
      setSubmitting(true);

      if (TEST_MODE) {
        console.log("[TEST_MODE] Submitting payload (wire):", wirePayload);
        await new Promise((r) => setTimeout(r, 600));
        const status = { ok: true, status: 200, id: "TEST-" + Math.random().toString(36).slice(2, 8) };
        setServerStatus(status);
        setLastSubmittedApp(appPayload);
        setSubmitOk(true);
        if (isWizard) setCurrentStep(3);
        return;
      }

      // 3) Post
      const status = await postLead(wirePayload);

      // 4) Update UI
      setServerStatus(status);
      if (!status.ok) throw new Error(status.message || "Unknown server error");

      // Persist what was sent so the confirmation reflects the exact data
      setLastSubmittedApp(appPayload);

      setSubmitOk(true);
      if (isWizard) setCurrentStep(3);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSubmitError(msg);
      setSubmitOk(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Clamp the active slide index to avoid accidental out-of-range access
  const active = slides[Math.min(slides.length - 1, Math.max(0, Number.isFinite(activeSlide) ? activeSlide : 0))];

  /* ------------------------------------------------------------
     UI
  ------------------------------------------------------------ */
  return (
    <div className="bg-body">
      {/* Inject mobile CSS for this demo file */}
      <style>{MOBILE_CSS}</style>

      {/* Sticky Topbar */}
      <Navbar expand="md" bg="light" className="border-bottom sticky-top">
        <Container>
          <Navbar.Brand href="#" className="fw-bold">
            Easy <span className="text-primary">Renters</span> Insurance
            {SHOW_VERSION && <Badge bg="secondary" className="ms-2">{VERSION}</Badge>}
          </Navbar.Brand>

          {/* [CHANGE] MOBILE: always-visible call button (outside collapse) */}
          <div className="d-md-none ms-auto me-2">
            <Button size="sm" variant="primary" href="tel:3033375434">
              Call {AGENT_PHONE_NUMBER}
            </Button>
          </div>

          <Navbar.Toggle aria-controls="topbar" />

          {/* DESKTOP: original nav content */}
          <Navbar.Collapse id="topbar" className="justify-content-end">
            <Nav className="align-items-center gap-2">
              <div className="d-none d-md-block text-body-secondary me-2">Speak with an agent:</div>
              <Button variant="outline-primary" href="tel:3033375434">{AGENT_PHONE_NUMBER}</Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO with background Carousel */}
      {/* [CHANGE] add .hero class (CSS controls min-height, responsive) */}
      <section id="home" className="position-relative overflow-hidden hero">
        <div className="position-absolute top-0 start-0 end-0 bottom-0" style={{ zIndex: 0 }} aria-hidden>
          <Carousel
            activeIndex={activeSlide}
            onSelect={(i) => setActiveSlide(typeof i === "number" ? i : 0)}
            controls
            indicators
            fade
            interval={3500}
            className="w-100 h-100"
          >
            {slides.map((s, idx) => (
              <Carousel.Item key={idx} className="w-100 h-100">
                <div
                  role="img"
                  aria-label={s.alt}
                  style={{
                    width: "100%",
                    height: "100%",
                    /* [CHANGE] remove inline minHeight so CSS .hero can control height on phones */
                    backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.55), rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.10) 60%, rgba(0,0,0,0.00)), url('${s.src}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <Carousel.Caption className="text-start" style={{ bottom: "40%" }} />
              </Carousel.Item>
            ))}
          </Carousel>
        </div>

        {/* Foreground content */}
        {/* [CHANGE] fluid container + smaller paddings/gutters on phones for max width usage */}
        <Container fluid className="position-relative px-2 px-sm-3 px-md-4" style={{ zIndex: 1 }}>
          {/* [CHANGE] reduce p-5 on phones -> p-3/p-sm-4/p-lg-5; reduce gutter on phones */}
          <Row className="align-items-start g-3 g-sm-4 p-3 p-sm-4 p-lg-5">
            <Col lg={6} className="text-white">
              <div className="pe-lg-4">
                <h1 className="display-5 fw-bold" style={{ marginTop: "400px" }}>{active.title}</h1>
                <p className="lead mb-3">{active.subline}</p>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Badge bg="light" text="dark">Fast Quotes</Badge>
                  <Badge bg="light" text="dark">Flexible Limits</Badge>
                  <Badge bg="light" text="dark">Licensed Advisors</Badge>
                </div>
                <div className="mt-4 d-flex gap-2">
                  <Button variant="outline-light" href="#faq">Common Questions</Button>
                </div>
              </div>
            </Col>

            {/* [CHANGE] Optional: show form first on phones for faster access */}
            <Col lg={6} className="order-first order-lg-last">
              {/* [CHANGE] add card-edge-mobile for edge-to-edge feel on xs */}
              <Card className="shadow-lg card-edge-mobile" style={{ backdropFilter: "blur(6px)" }}>
                <Card.Header className="bg-white">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="fw-semibold">Fast, No-Obligation Quote</div>
                      <small className="text-body-secondary">Tell us about your place to get options.</small>
                    </div>
                    <Badge bg="success">Secure</Badge>
                  </div>
                </Card.Header>
                <Card.Body ref={formRef}>
                  {submitError && (
                    <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>
                      Server: {submitError}
                    </Alert>
                  )}

                  {/* MODE TOGGLE + (Wizard) Stepper */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="switch"
                        id="modeSwitch"
                        label={isWizard ? "Wizard mode" : "Vertical mode"}
                        checked={isWizard}
                        onChange={(e) => {
                          setMode(e.currentTarget.checked ? "wizard" : "vertical");
                          setTouched({});
                          if (submitOk && e.currentTarget.checked) setCurrentStep(3);
                          else setCurrentStep(0);
                        }}
                      />
                      <small className="text-muted">Switch to see all fields in one view.</small>
                    </div>

                    <div style={{ minWidth: 280 }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Badge bg="secondary">Step {isWizard ? currentStep + 1 : (submitOk ? 2 : 1)} of {isWizard ? 4 : (submitOk ? 2 : 1)}</Badge>
                        <small className="text-muted">{isWizard ? steps[currentStep] : (submitOk ? "Confirmation" : "All fields")}</small>
                      </div>
                      <ProgressBar now={isWizard ? ((currentStep + 1) / 4) * 100 : (submitOk ? 100 : 50)} />
                    </div>
                  </div>

                  {/* RENTERS FORM START */}
                  <Form
                    onSubmit={handleSubmit}
                    noValidate
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && isWizard) {
                        if (currentStep < 2) {
                          e.preventDefault();
                          e.stopPropagation();
                          if (isStepValid(currentStep)) goNext();
                        }
                      }
                    }}
                  >
                    {isWizard ? (
                      <>
                        <Row className="g-3">
                          {/* STEP 1: CONTACT & ADDRESS */}
                          {currentStep === 0 && (
                            <Col xs={12}>
                              <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                  <strong>Contact &amp; Address</strong>
                                </legend>

                                <Row className="g-3 mt-1">
                                  <Col md={12}>
                                    <Form.Label htmlFor="fullName">Full Name</Form.Label>
                                    <Form.Control
                                      id="fullName"
                                      value={fullName}
                                      onChange={(e) => setFullName(e.target.value)}
                                      placeholder="Jane Doe"
                                      required
                                      {...vp("fullName", V.fullName)}
                                    />
                                    <Form.Control.Feedback type="invalid">Please enter your full name.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="email">Email</Form.Label>
                                    <Form.Control
                                      id="email"
                                      type="email"
                                      value={email}
                                      onChange={(e) => setEmail(e.target.value)}
                                      placeholder="you@example.com"
                                      required
                                      {...vp("email", V.email)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="phone">Phone</Form.Label>
                                    <Form.Control
                                      id="phone"
                                      inputMode="tel"
                                      value={phone}
                                      onChange={(e) => setPhone(e.target.value)}
                                      placeholder="(555) 123-4567"
                                      required
                                      {...vp("phone", V.phone)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter a 10-digit phone.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={8}>
                                    <Form.Label htmlFor="address1">Street Address</Form.Label>
                                    <Form.Control
                                      id="address1"
                                      value={address1}
                                      onChange={(e) => setAddress1(e.target.value)}
                                      placeholder="123 Main St"
                                      required
                                      {...vp("address1", V.address1)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter your street address.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="address2">Unit / Apt</Form.Label>
                                    <Form.Control
                                      id="address2"
                                      value={address2}
                                      onChange={(e) => setAddress2(e.target.value)}
                                      placeholder="Apt 2B"
                                      {...vpOpt("address2", !!address2.trim())}
                                    />
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="city">City</Form.Label>
                                    <Form.Control
                                      id="city"
                                      value={city}
                                      onChange={(e) => setCity(e.target.value)}
                                      placeholder="City"
                                      required
                                      {...vp("city", V.city)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter your city.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="state">State</Form.Label>
                                    <Form.Select
                                      id="state"
                                      value={state}
                                      onChange={(e) => setState(e.target.value)}
                                      required
                                      {...vp("state", V.state)}
                                    >
                                      <option value="">Select</option>
                                      {US_STATES.map((s) => (
                                        <option key={s.code} value={s.code}>{s.name}</option>
                                      ))}
                                    </Form.Select>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="zipCode">Zip</Form.Label>
                                    <Form.Control
                                      id="zipCode"
                                      inputMode="numeric"
                                      value={zipCode}
                                      onChange={(e) => setZipCode(e.target.value)}
                                      placeholder="#####"
                                      required
                                      {...vp("zip", V.zip)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter a 5-digit zip code.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="resType">Residence Type</Form.Label>
                                    <Form.Select
                                      id="resType"
                                      value={residenceType}
                                      onChange={(e) => setResidenceType(e.target.value as ResidenceType)}
                                      required
                                      {...vp("residenceType", V.residenceType)}
                                    >
                                      <option value="">Select type</option>
                                      <option value="apartment">Apartment</option>
                                      <option value="condo">Condo</option>
                                      <option value="house">House</option>
                                      <option value="townhome">Townhome</option>
                                      <option value="duplex">Duplex</option>
                                      <option value="other">Other</option>
                                    </Form.Select>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="moveIn">Move-In Date</Form.Label>
                                    <Form.Control
                                      id="moveIn"
                                      type="date"
                                      value={moveInDate}
                                      onChange={(e) => setMoveInDate(e.target.value)}
                                      {...vpOpt("moveInDate", !!moveInDate)}
                                    />
                                    <div className="form-text">(Estimated/Optional)</div>
                                  </Col>
                                </Row>
                              </fieldset>
                            </Col>
                          )}

                          {/* STEP 2: COVERAGE & HOUSEHOLD */}
                          {currentStep === 1 && (
                            <Col xs={12}>
                              <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                  <strong>Coverage &amp; Household</strong>
                                </legend>

                                <Row className="g-3 mt-1">
                                  <Col md={4}>
                                    <Form.Label htmlFor="ppCoverage">Personal Property</Form.Label>
                                    <InputGroup hasValidation>
                                      <InputGroup.Text>$</InputGroup.Text>
                                      <Form.Control
                                        id="ppCoverage"
                                        inputMode="numeric"
                                        value={personalPropertyCoverage}
                                        onChange={(e) => setPersonalPropertyCoverage(e.target.value)}
                                        placeholder="e.g., 30000"
                                        required
                                        {...vp("personalPropertyCoverage", V.personalPropertyCoverage)}
                                      />
                                      <Form.Control.Feedback type="invalid">Enter amount &gt; 0.</Form.Control.Feedback>
                                    </InputGroup>
                                    <div className="form-text">Covers your belongings (furniture, clothing, electronics).</div>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="liability">Liability</Form.Label>
                                    <InputGroup hasValidation>
                                      <InputGroup.Text>$</InputGroup.Text>
                                      <Form.Control
                                        id="liability"
                                        inputMode="numeric"
                                        value={liabilityCoverage}
                                        onChange={(e) => setLiabilityCoverage(e.target.value)}
                                        placeholder="e.g., 300000"
                                        required
                                        {...vp("liabilityCoverage", V.liabilityCoverage)}
                                      />
                                      <Form.Control.Feedback type="invalid">Min $100,000.</Form.Control.Feedback>
                                    </InputGroup>
                                    <div className="form-text">Protects you if someone is injured and you’re liable.</div>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="deductible">Deductible</Form.Label>
                                    <Form.Select
                                      id="deductible"
                                      value={deductible}
                                      onChange={(e) => setDeductible(e.target.value as Deductible)}
                                      required
                                      {...vp("deductible", V.deductible)}
                                    >
                                      <option value="">Select</option>
                                      <option value="250">$250</option>
                                      <option value="500">$500</option>
                                      <option value="1000">$1,000</option>
                                      <option value="1500">$1,500</option>
                                      <option value="2500">$2,500</option>
                                    </Form.Select>
                                    <div className="form-text">Your agent will help you find the best value.</div>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="roommates">Roommates</Form.Label>
                                    <Form.Control
                                      id="roommates"
                                      inputMode="numeric"
                                      value={roommatesCount}
                                      onChange={(e) => setRoommatesCount(Number(e.target.value))}
                                      placeholder="0"
                                      required
                                      {...vp("roommatesCount", V.roommatesCount)}
                                    />
                                    <Form.Control.Feedback type="invalid">0–10 roommates allowed.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="claims">Prior Claims</Form.Label>
                                    <Form.Control
                                      id="claims"
                                      inputMode="numeric"
                                      value={priorClaimsCount5y}
                                      onChange={(e) => setPriorClaimsCount5y(Number(e.target.value))}
                                      placeholder="0"
                                      required
                                      {...vp("priorClaimsCount5y", V.priorClaimsCount5y)}
                                    />
                                    <Form.Control.Feedback type="invalid">0–10 allowed.</Form.Control.Feedback>
                                    <div className="form-text">( Number of claims in the past 5 years )</div>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="pets">Pets</Form.Label>
                                    <Form.Control
                                      id="pets"
                                      type="text"
                                      value={pets}
                                      onChange={(e) => setPets(e.target.value)}
                                      placeholder="e.g., None, 1 dog, 2 cats"
                                      {...vp("pets", V.pets)}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      Please enter at least 2 characters or leave it blank.
                                    </Form.Control.Feedback>
                                    <div className="form-text">( optional )</div>
                                  </Col>
                                </Row>
                              </fieldset>
                            </Col>
                          )}

                          {/* STEP 3: PROPERTY & NOTES */}
                          {currentStep === 2 && (
                            <>
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Property Details</strong>
                                  </legend>

                                  <Row className="g-3 mt-1">
                                    <Col md={3}>
                                      <Form.Label htmlFor="yearBuilt">Year Built</Form.Label>
                                      <Form.Control
                                        id="yearBuilt"
                                        inputMode="numeric"
                                        value={yearBuilt}
                                        onChange={(e) => setYearBuilt(e.target.value)}
                                        placeholder="e.g., 1998"
                                        {...vp("yearBuilt", V.yearBuilt)}
                                      />
                                      <div className="form-text">4 digit year<br />optional.</div>
                                    </Col>

                                    <Col md={3}>
                                      <Form.Label htmlFor="unitFloor">Unit Floor </Form.Label>
                                      <Form.Control
                                        id="unitFloor"
                                        inputMode="numeric"
                                        value={unitFloor}
                                        onChange={(e) => setUnitFloor(e.target.value)}
                                        placeholder="e.g., 3"
                                        {...vp("unitFloor", V.unitFloor)}
                                      />
                                      <div className="form-text">( optional )</div>
                                    </Col>

                                    <Col md={6} className="pt-md-4">
                                      <div className="d-flex flex-wrap gap-3">
                                        <Form.Check
                                          id="smokeAlarms"
                                          type="checkbox"
                                          label="Smoke Alarms"
                                          checked={hasSmokeAlarms}
                                          onChange={(e) => setHasSmokeAlarms(e.currentTarget.checked)}
                                        />
                                        <Form.Check
                                          id="deadbolts"
                                          type="checkbox"
                                          label="Deadbolts"
                                          checked={hasDeadbolts}
                                          onChange={(e) => setHasDeadbolts(e.currentTarget.checked)}
                                        />
                                        <Form.Check
                                          id="monitored"
                                          type="checkbox"
                                          label="Monitored Alarm"
                                          checked={hasMonitoredAlarm}
                                          onChange={(e) => setHasMonitoredAlarm(e.currentTarget.checked)}
                                        />
                                      </div>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="landlord">Landlord / Property Mgmt</Form.Label>
                                      <Form.Control
                                        id="landlord"
                                        value={landlordName}
                                        onChange={(e) => setLandlordName(e.target.value)}
                                        placeholder="Name"
                                        {...vp("landlordName", V.landlordName)}
                                      />
                                      <div className="form-text">(Optional)</div>
                                    </Col>

                                    <Col md={12}>
                                      <Form.Label htmlFor="notes">Notes (optional)</Form.Label>
                                      <Form.Control
                                        id="notes"
                                        as="textarea"
                                        rows={3}
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder="Other helpful / important information?"
                                        {...vp("notes", V.notes)}
                                      />
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>
                            </>
                          )}

                          {/* STEP 4: CONFIRMATION */}
                          {currentStep === 3 && (
                            <Col xs={12}>
                              <ConfirmationBlock
                                status={serverStatus}
                                app={lastSubmittedApp}
                              />
                            </Col>
                          )}
                        </Row>

                        {/* Wizard controls (no buttons on final step) */}
                        {currentStep < 3 && (
                          <div className="d-flex justify-content-between mt-3">
                            <Button
                              type="button"
                              variant="outline-secondary"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                goBack();
                              }}
                              disabled={currentStep === 0}
                            >
                              Previous
                            </Button>

                            {currentStep < 2 ? (
                              <Button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (isStepValid(currentStep)) goNext();
                                }}
                                disabled={!isStepValid(currentStep)}
                              >
                                Next
                              </Button>
                            ) : (
                              <Button
                                type="submit"
                                variant="primary"
                                size="lg"
                                disabled={submitting || !valid}
                              >
                                {submitting ? (
                                  <>
                                    <Spinner animation="border" size="sm" className="me-2" /> Submitting...
                                  </>
                                ) : (
                                  <>Get My Quote</>
                                )}
                              </Button>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      /* ---------- VERTICAL (all-in-one) ---------- */
                      <>
                        {!submitOk ? (
                          <>
                            <Row className="g-3">
                              {/* Contact & Address */}
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Contact &amp; Address</strong>
                                  </legend>

                                  <Row className="g-3 mt-1">
                                    <Col md={12}>
                                      <Form.Label htmlFor="fullNameV">Full Name</Form.Label>
                                      <Form.Control
                                        id="fullNameV"
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        placeholder="Jane Doe"
                                        required
                                        {...vp("fullName", V.fullName)}
                                      />
                                      <Form.Control.Feedback type="invalid">Please enter your full name.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="emailV">Email</Form.Label>
                                      <Form.Control
                                        id="emailV"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        required
                                        {...vp("email", V.email)}
                                      />
                                      <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="phoneV">Phone</Form.Label>
                                      <Form.Control
                                        id="phoneV"
                                        inputMode="tel"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="(555) 123-4567"
                                        required
                                        {...vp("phone", V.phone)}
                                      />
                                      <Form.Control.Feedback type="invalid">Enter a 10-digit phone.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={8}>
                                      <Form.Label htmlFor="address1V">Street Address</Form.Label>
                                      <Form.Control
                                        id="address1V"
                                        value={address1}
                                        onChange={(e) => setAddress1(e.target.value)}
                                        placeholder="123 Main St"
                                        required
                                        {...vp("address1", V.address1)}
                                      />
                                      <Form.Control.Feedback type="invalid">Enter your street address.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="address2V">Unit / Apt</Form.Label>
                                      <Form.Control
                                        id="address2V"
                                        value={address2}
                                        onChange={(e) => setAddress2(e.target.value)}
                                        placeholder="Apt 2B"
                                        {...vpOpt("address2", !!address2.trim())}
                                      />
                                      <div className="form-text">( optional )</div>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="cityV">City</Form.Label>
                                      <Form.Control
                                        id="cityV"
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                        placeholder="City"
                                        required
                                        {...vp("city", V.city)}
                                      />
                                      <Form.Control.Feedback type="invalid">Enter your city.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="stateV">State</Form.Label>
                                      <Form.Select
                                        id="stateV"
                                        value={state}
                                        onChange={(e) => setState(e.target.value)}
                                        required
                                        {...vp("state", V.state)}
                                      >
                                        <option value="">Select</option>
                                        {US_STATES.map((s) => (
                                          <option key={s.code} value={s.code}>{s.name}</option>
                                        ))}
                                      </Form.Select>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="zipCodeV">Zip</Form.Label>
                                      <Form.Control
                                        id="zipCodeV"
                                        inputMode="numeric"
                                        value={zipCode}
                                        onChange={(e) => setZipCode(e.target.value)}
                                        placeholder="#####"
                                        required
                                        {...vp("zip", V.zip)}
                                      />
                                      <Form.Control.Feedback type="invalid">Enter a 5-digit zip code.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="resTypeV">Residence Type</Form.Label>
                                      <Form.Select
                                        id="resTypeV"
                                        value={residenceType}
                                        onChange={(e) => setResidenceType(e.target.value as ResidenceType)}
                                        required
                                        {...vp("residenceType", V.residenceType)}
                                      >
                                        <option value="">Select type</option>
                                        <option value="apartment">Apartment</option>
                                        <option value="condo">Condo</option>
                                        <option value="house">House</option>
                                        <option value="townhome">Townhome</option>
                                        <option value="duplex">Duplex</option>
                                        <option value="other">Other</option>
                                      </Form.Select>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="moveInV">Move-In Date</Form.Label>
                                      <Form.Control
                                        id="moveInV"
                                        type="date"
                                        value={moveInDate}
                                        onChange={(e) => setMoveInDate(e.target.value)}
                                        {...vpOpt("moveInDate", !!moveInDate)}
                                      />
                                      <div className="form-text">(Estimated/Optional)</div>
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>

                              {/* Coverage & Household */}
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Coverage &amp; Household</strong>
                                  </legend>

                                  <Row className="g-3 mt-1">
                                    <Col md={4}>
                                      <Form.Label htmlFor="ppCoverageV">Personal Property</Form.Label>
                                      <InputGroup hasValidation>
                                        <InputGroup.Text>$</InputGroup.Text>
                                        <Form.Control
                                          id="ppCoverageV"
                                          inputMode="numeric"
                                          value={personalPropertyCoverage}
                                          onChange={(e) => setPersonalPropertyCoverage(e.target.value)}
                                          placeholder="e.g., 30000"
                                          required
                                          {...vp("personalPropertyCoverage", V.personalPropertyCoverage)}
                                        />
                                        <Form.Control.Feedback type="invalid">Enter amount &gt; 0.</Form.Control.Feedback>
                                      </InputGroup>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="liabilityV">Liability</Form.Label>
                                      <InputGroup hasValidation>
                                        <InputGroup.Text>$</InputGroup.Text>
                                        <Form.Control
                                          id="liabilityV"
                                          inputMode="numeric"
                                          value={liabilityCoverage}
                                          onChange={(e) => setLiabilityCoverage(e.target.value)}
                                          placeholder="e.g., 300000"
                                          required
                                          {...vp("liabilityCoverage", V.liabilityCoverage)}
                                        />
                                        <Form.Control.Feedback type="invalid">Min $100,000.</Form.Control.Feedback>
                                      </InputGroup>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="deductibleV">Deductible</Form.Label>
                                      <Form.Select
                                        id="deductibleV"
                                        value={deductible}
                                        onChange={(e) => setDeductible(e.target.value as Deductible)}
                                        required
                                        {...vp("deductible", V.deductible)}
                                      >
                                        <option value="">Select</option>
                                        <option value="250">$250</option>
                                        <option value="500">$500</option>
                                        <option value="1000">$1,000</option>
                                        <option value="1500">$1,500</option>
                                        <option value="2500">$2,500</option>
                                      </Form.Select>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="roommatesV">Roommates</Form.Label>
                                      <Form.Control
                                        id="roommatesV"
                                        inputMode="numeric"
                                        value={roommatesCount}
                                        onChange={(e) => setRoommatesCount(Number(e.target.value))}
                                        placeholder="0"
                                        required
                                        {...vp("roommatesCount", V.roommatesCount)}
                                      />
                                      <Form.Control.Feedback type="invalid">0–10 roommates allowed.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="claimsV">Prior Claims</Form.Label>
                                      <Form.Control
                                        id="claimsV"
                                        inputMode="numeric"
                                        value={priorClaimsCount5y}
                                        onChange={(e) => setPriorClaimsCount5y(Number(e.target.value))}
                                        placeholder="0"
                                        required
                                        {...vp("priorClaimsCount5y", V.priorClaimsCount5y)}
                                      />
                                      <Form.Control.Feedback type="invalid">0–10 allowed.</Form.Control.Feedback>
                                      <div className="form-text">( Number of claims in the past 5 years )</div>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="petsV">Pets (optional)</Form.Label>
                                      <Form.Control
                                        id="petsV"
                                        type="text"
                                        value={pets}
                                        onChange={(e) => setPets(e.target.value)}
                                        placeholder="e.g., None, 1 dog, 2 cats"
                                        {...vp("pets", V.pets)}
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        Please enter at least 2 characters or leave it blank.
                                      </Form.Control.Feedback>
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>

                              {/* Property & Notes */}
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Property Details &amp; Notes</strong>
                                  </legend>
                                  <Row className="g-3 mt-1">
                                    <Col md={3}>
                                      <Form.Label htmlFor="yearBuiltV">Year Built </Form.Label>
                                      <Form.Control
                                        id="yearBuiltV"
                                        inputMode="numeric"
                                        value={yearBuilt}
                                        onChange={(e) => setYearBuilt(e.target.value)}
                                        placeholder="e.g., 1998"
                                        {...vp("yearBuilt", V.yearBuilt)}
                                      />
                                      <div className="form-text">4 digit year<br />optional.</div>
                                    </Col>

                                    <Col md={3}>
                                      <Form.Label htmlFor="unitFloorV">Unit Floor </Form.Label>
                                      <Form.Control
                                        id="unitFloorV"
                                        inputMode="numeric"
                                        value={unitFloor}
                                        onChange={(e) => setUnitFloor(e.target.value)}
                                        placeholder="e.g., 3"
                                        {...vp("unitFloor", V.unitFloor)}
                                      />
                                      <div className="form-text">( optional )</div>
                                    </Col>

                                    <Col md={6} className="pt-md-4">
                                      <div className="d-flex flex-wrap gap-3">
                                        <Form.Check
                                          id="smokeAlarmsV"
                                          type="checkbox"
                                          label="Smoke Alarms"
                                          checked={hasSmokeAlarms}
                                          onChange={(e) => setHasSmokeAlarms(e.currentTarget.checked)}
                                        />
                                        <Form.Check
                                          id="deadboltsV"
                                          type="checkbox"
                                          label="Deadbolts"
                                          checked={hasDeadbolts}
                                          onChange={(e) => setHasDeadbolts(e.currentTarget.checked)}
                                        />
                                        <Form.Check
                                          id="monitoredV"
                                          type="checkbox"
                                          label="Monitored Alarm"
                                          checked={hasMonitoredAlarm}
                                          onChange={(e) => setHasMonitoredAlarm(e.currentTarget.checked)}
                                        />
                                      </div>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="landlordV">Landlord / Property Mgmt</Form.Label>
                                      <Form.Control
                                        id="landlordV"
                                        value={landlordName}
                                        onChange={(e) => setLandlordName(e.target.value)}
                                        placeholder="Name"
                                        {...vp("landlordName", V.landlordName)}
                                      />
                                      <div className="form-text">(optional)</div>
                                    </Col>

                                    <Col md={12}>
                                      <Form.Label htmlFor="notesV">Notes (optional)</Form.Label>
                                      <Form.Control
                                        id="notesV"
                                        as="textarea"
                                        rows={3}
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder="Other helpful / important information?"
                                        {...vp("notes", V.notes)}
                                      />
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>
                            </Row>

                            {/* Submit */}
                            <div className="d-grid mt-3">
                              <Button type="submit" variant="primary" size="lg" disabled={!valid || submitting}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                              </Button>
                              <div className="text-center mt-2 text-body-secondary" style={{ fontSize: "0.9rem" }}>
                                By clicking, you agree we may contact you about insurance options. No spam. You can opt out anytime.
                              </div>
                            </div>
                          </>
                        ) : (
                          <ConfirmationBlock
                            status={serverStatus}
                            app={lastSubmittedApp}
                          />
                        )}
                      </>
                    )}
                  </Form>
                  {/* RENTERS FORM END */}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works */}
      <section className="py-5 bg-light border-top border-bottom">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">How It Works</h2>
              <p className="text-body-secondary mb-0">Three simple steps to get covered.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">1</div>
                <div>
                  <h6 className="mb-1">Tell us about your place</h6>
                  <p className="mb-0 text-body-secondary">Share address, residence type, and who lives there.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">2</div>
                <div>
                  <h6 className="mb-1">Pick coverage</h6>
                  <p className="mb-0 text-body-secondary">Choose personal property, liability, and deductible.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">3</div>
                <div>
                  <h6 className="mb-1">We compare options</h6>
                  <p className="mb-0 text-body-secondary">We shop carriers and help you bind the best-fit policy.</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Spacer so the fixed call bar doesn't cover the bottom of content on mobile */}
      {/* [CHANGE] This ensures the footer isn't obscured by the fixed action bar on small screens */}
      <div className="d-md-none" style={{ height: "64px" }} />

      {/* FAQ */}
      <section id="faq" className="py-5 bg-light border-top">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">Frequently Asked Questions</h2>
              <p className="text-body-secondary mb-0">Quick answers about renters insurance.</p>
            </Col>
          </Row>
          <FAQ_Component_v02b_Simple faqs={rentersInsuranceFAQs} openFirst />
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="small text-body-secondary">
              © {new Date().getFullYear()} Easy Renters Insurance. All rights reserved.
              <div className="mt-2">
                Insurance services subject to licensing and state availability. This page is for general information and not legal advice.
              </div>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end mt-3 mt-md-0 gap-2">
              <Button variant="secondary" size="sm" href="tel:3033375434">Call {AGENT_PHONE_NUMBER}</Button>
            </Col>
          </Row>
        </Container>
      </footer>

      {/* [CHANGE] Fixed mobile call bar */}
      <div className="mobile-callbar d-md-none">
        <div className="mobile-callbar-inner">
          <Button href="tel:3033375434" size="lg" className="w-100">
            Call {AGENT_PHONE_NUMBER}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------
   Confirmation block (Sectioned, readable, no JSON)
------------------------------------------------------------ */
const ConfirmationBlock: React.FC<{
  status: { ok: boolean; status?: number; id?: string; message?: string } | null;
  app: ILeadPayload | null;
}> = ({ status, app }) => {
  const ok = !!status?.ok;

  if (!app) {
    return (
      <Card className="border-0">
        <Card.Body className="p-4">
          <Alert variant="warning" className="mb-0">We don’t have your details to display yet.</Alert>
        </Card.Body>
      </Card>
    );
  }

  const safetyList = [
    app.hasSmokeAlarms ? "Smoke Alarms" : null,
    app.hasDeadbolts ? "Deadbolts" : null,
    app.hasMonitoredAlarm ? "Monitored Alarm" : null,
  ].filter(Boolean) as string[];

  return (
    <Card className="border-0">
      <Card.Body className="p-4">
        {/* Status */}
        {ok ? (
          <Alert variant="success" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">Thank you! A licensed representative will contact you shortly.</div>
              <div className="small">
                Submission status: <strong>{status?.status ?? 200}</strong>
                {status?.id ? <> • Reference ID: <strong>{status.id}</strong></> : null}
              </div>
            </div>
          </Alert>
        ) : (
          <Alert variant="warning" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">We couldn’t confirm your submission.</div>
              <div className="small">{status?.message || "Please try again or give us a call."}</div>
            </div>
          </Alert>
        )}

        {/* Sections */}
        <div className="mt-3">
          {/* Contact */}
          <Section title="Contact">
            <KV label="Name"  value={dash(app.fullName)} />
            <KV label="Email" value={dash(app.email)} />
            <KV label="Phone" value={formatPhonePretty(app.phone)} />
          </Section>

          {/* Address */}
          <Section title="Address">
            <KV label="Street"            value={dash(app.address1)} />
            <KV label="Unit / Apt"        value={dash(app.address2)} />
            <KV label="City / State / Zip" value={`${dash(app.city)}, ${dash(app.state)} ${dash(app.zipCode)}`} />
            <KV label="Residence Type"    value={cap(dash(app.residenceType))} />
            <KV label="Move-In Date"      value={dash(app.moveInDate)} />
          </Section>

          {/* Coverage */}
          <Section title="Coverage">
            <KV label="Personal Property" value={money(app.personalPropertyCoverage)} />
            <KV label="Liability"         value={money(app.liabilityCoverage)} />
            <KV label="Deductible"        value={app.deductible ? money(app.deductible) : "—"} />
          </Section>

          {/* Household */}
          <Section title="Household">
            <KV label="Roommates"         value={String(app.roommatesCount)} />
            <KV label="Prior Claims (5y)" value={String(app.priorClaimsCount5y)} />
            <KV label="Pets"              value={dash(app.pets)} />
          </Section>

          {/* Property & Safety */}
          <Section title="Property & Safety">
            <KV label="Year Built"        value={dash(app.yearBuilt)} />
            <KV label="Unit Floor"        value={dash(app.unitFloor)} />
            <KV label="Safety Features"   value={safetyList.length ? safetyList.join(", ") : "—"} />
            <KV label="Landlord / Mgmt"   value={dash(app.landlordName)} />
          </Section>

          {/* Notes */}
          <Section title="Notes">
            <div className="p-3 bg-body-tertiary border rounded">{dash(app.notes)}</div>
          </Section>
        </div>

        {/* Actions */}
        <div className="mt-4 d-flex flex-wrap gap-2">
          <Button href="tel:3033375434" variant="primary">Call {AGENT_PHONE_NUMBER}</Button>
          <Button
            variant="outline-secondary"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            Back to top
          </Button>
          {
          /* <Button
            variant="outline-secondary"
            onClick={() => window.print()}
          >
            Print
          </Button> */
          }
        </div>
      </Card.Body>
    </Card>
  );
};

/* ---------- Tiny helpers for layout ---------- */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-4">
    <h6 className="text-uppercase text-muted mb-2">{title}</h6>
    <Row as="dl" className="row-cols-1 row-cols-md-2 g-3 mb-0">
      {children}
    </Row>
    <hr className="mt-3 mb-0" />
  </div>
);

const KV: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Col className="mb-0">
    <dt className="small text-muted mb-1">{label}</dt>
    <dd className="mb-0">{value}</dd>
  </Col>
);

export default EASY_RENTERS_LandingPage_v02P_WIZARD;
